/**
 * @file The primary entry path: a textarea of pasted codes and one Add button. Consumer:
 * `SignifyFetchJobEntry`. Nothing here calls Signify — the parts are staged and resolved later.
 * The button's label is the whole of its state: it names how many parts would be added, how many
 * are already in the list, or the reason it cannot add, so a disabled button always says why.
 * Label and add run the same `planSignifyFetchJobAdditions`, so the label cannot promise what the
 * add does not do. It subscribes to the row count and to `keyOwners`, the two leaves that move the
 * numbers, and never to a row — a quantity edit does not re-render it.
 */

import { FC, useMemo, useState } from 'react';
import { Button, Input, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import { SIGNIFY_FETCH_JOB_MAX_INPUTS } from '#types';
import FlexCol from '@/components/atoms/FlexCol';
import { parsePastedSignifyCodes, planSignifyFetchJobAdditions } from '../use/services';
import type { SignifyFetchJobAddPlan, SignifyFetchJobDraft, SignifyFetchJobDraftEntry } from '../index.types';

const { Text } = Typography;

const partsLabel = (count: number): string => `${count} ${count === 1 ? 'part' : 'parts'}`;

type SignifyFetchJobPasteInputProps = {
  draft: SignifyFetchJobDraft;
  distributorSelected: boolean;
  onAdd: (entries: readonly SignifyFetchJobDraftEntry[]) => SignifyFetchJobAddPlan;
};

const SignifyFetchJobPasteInput: FC<SignifyFetchJobPasteInputProps> = ({
  draft,
  distributorSelected,
  onAdd,
}) => {
  const [text, setText] = useState<string>('');
  const { keys, keyOwners } = useSnapshot(draft);

  const parsed = useMemo(() => parsePastedSignifyCodes(text), [text]);
  const plan = useMemo(
    () => planSignifyFetchJobAdditions({ entries: parsed, keyOwners, currentCount: keys.length }),
    [parsed, keyOwners, keys.length],
  );

  const disabledReason = !distributorSelected
    ? 'Choose a distributor first'
    : parsed.length === 0
      ? 'Paste codes to add'
      : plan.accepted.length === 0
        ? (plan.duplicates > 0 ? `All ${partsLabel(plan.duplicates)} already in the list` : 'Nothing to add')
        : plan.exceedsCap
          ? `Adding ${plan.accepted.length} would exceed ${SIGNIFY_FETCH_JOB_MAX_INPUTS} (${plan.free} free)`
          : null;

  const label =
    disabledReason ??
    (plan.duplicates > 0
      ? `Add ${partsLabel(plan.accepted.length)} (${plan.duplicates} duplicates skipped)`
      : `Add ${partsLabel(plan.accepted.length)}`);

  const handleAdd = () => {
    const applied = onAdd(parsePastedSignifyCodes(text));
    if (applied.accepted.length > 0) setText('');
  };

  return (
    <FlexCol gap={5}>
      <Input.TextArea
        aria-label="signify-fetch-job-paste-codes"
        value={text}
        onChange={(event) => setText(event.target.value)}
        autoSize={{ minRows: 4, maxRows: 10 }}
        placeholder="One part per line. Optional quantity after a tab or comma: 8710163369648, 12"
      />
      <Button
        type="primary"
        aria-label="signify-fetch-job-add-pasted-parts"
        disabled={!!disabledReason}
        onClick={handleAdd}
      >
        {label}
      </Button>
      <Text type="secondary">A commercial code with spaces stays one part.</Text>
    </FlexCol>
  );
};

export default SignifyFetchJobPasteInput;
